import {capitalize, defaultsDeep, find, get, isArray, map, set, setWith, trimEnd, last, toPath, merge} from 'lodash';
import moment from 'moment';
import Vue from "vue";
import i18n from 'src/i18n/index';
import {parseError, formatPhone} from 'src/utils';
import sweetalert from 'sweetalert2';
import * as ServiceLocation from './service_location';
import {MessageBox} from 'element-ui';


/**
 * define possible types
 */
export const fieldDefaults = {
  string: {component: 'f-input', inputType: 'text'},
  phone: {
    component: 'f-input', inputType: 'text', get(model, spec) {
      return formatPhone(get(model, spec.prop));
    },
  },
  text: {component: 'f-textarea', list: false},
  number: {component: 'f-input', inputType: 'number'},
  boolean: {
    component: 'f-checkbox',
    filter: [{label: 'action.yes', value: true}, {label: 'action.no', value: false}],
  },
  date: {component: 'f-datetime', t: 'date'},
  time: {component: 'f-datetime', t: 'time'},
  datetime: {component: 'f-datetime', t: 'datetime'},
  email: {component: 'f-input', inputType: 'email'},
  id: {component: 'f-id', list: false},
  label: {component: 'f-label', readonly: true},
  integer: {component: 'f-input', inputType: 'number', step: 1, validation: {numeric: true}},
  password: {component: 'f-input', inputType: 'password'},
  select: {
    component: 'f-select', postprocess(spec) {
      if (isArray(spec.items)) {
        spec.items = map(spec.items, k => {
          if (typeof k === 'string') {
            let v = last(k.split('.'));
            return {label: i18n.fr(k), value: v};
          } else if (typeof k === 'object') {
            return {...k, label: i18n.fr(k.label)};
          }
        });
        spec.filter = map(spec.items, x => ({label: x.label, value: x.value}));
      }
      return spec;
    },
  },
  radio: {component: 'f-radio'},
  checkbox: {
    component: 'f-checkbox',
    filter: [{label: 'action.yes', value: true}, {label: 'action.no', value: false}],
  },
  tags: {component: 'f-tags', list: false, hidden: true},
  schema: {component: 'f-schema', list: false, showActions: false},
  array: {
    component: 'f-array', list: false, postprocess(spec) {
      if (spec.itemType) {
        spec.itemType = applyFieldDefaults(spec.itemType);
      }
      return spec;
    },
  },
  link: {component: 'f-link', list: false},
  img: {component: 'f-img', list: false},
  file: {component: 'f-file', list: false},
  button: {component: 'f-button', list: false},
  table: {component: 'f-table', list: false, embed: false},
  i18n: {
    component: 'f-i18n', postprocess(spec) {
      if (spec.itemType) {
        spec.itemType = applyFieldDefaults(spec.itemType);
      }
      return spec;
    },
  },
  json: {component: 'f-json', list: false},
  html: {component: 'f-html', list: false},
};

/**
 * @param {Object|Function} dbSchema
 * @return {columns}
 */
export function columnsFn(dbSchema) {
  return function (model, schema, readonly, table) {
    let temp = [];
    let columns = typeof dbSchema === 'function' ? dbSchema.call(this, model, schema, readonly, table) : dbSchema;

    for (let k of Object.keys(columns)) {
      let field = {...columns[k]}; // clone
      let type = field.type;
      switch (field.type) {
        case String:
        case Number:
        case Boolean:
          type = field.type.name.toLowerCase();
          break;
        case Date:
          type = 'datetime';
      }
      if (fieldDefaults[type]) {
        let spec = {
          label: i18n.s('fields.' + last(k.split('.')), k.split('_').map(capitalize).join(' ')),
          prop: k,
          type: type,
          display: displayField,
        };
        field.type = type;
        if (field.required) {
          spec.validation = {required: true};
        }
        field = defaultsDeep(field, spec);
        field.label = isArray(field.label) ? i18n.fr.apply(i18n, field.label) : i18n.fr(field.label);
        temp.push(field);
      }
    }

    return applyFieldDefaults(temp);
  };
}

export function inlineSchema(dbSchema) {
  return {
    columns: columnsFn(dbSchema),
  };
}

export function getField(model, spec) {
  return typeof spec.get === 'function'
    ? spec.get.call(this, model, spec)
    : get(model, spec.prop);
}

export function setField(model, spec, value) {
  let _set = this.$vueSet || this.$set || set;
  if (typeof spec.set === 'function') {
    return spec.set.call(this, model, spec, value, _set);
  } else {
    try {
      _set(model, spec.prop, value);
    } catch (e) {
      console.warn(e);
    }
  }
}

/**
 * format a field for user friendly display
 * @return {string|object|Function} plain string, component, or render function
 */
export function displayField(model, index, fieldSpec, schema, columns) {
  let value = getField.call(this, model, fieldSpec);
  if (value == null) {
    return {template: i18n.f('html.nil')};
  }
  let m;
  switch (fieldSpec.type) {
    case 'string':
    case 'number':
      return String(value);
    case 'boolean':
    case 'checkbox':
      return value ? {template: '<i class="ti-check"></i>'} : '-';
    case 'date':
      m = moment(value).local();
      return {template: `<span title="${m.fromNow()}">${m.format('DD/MM/YYYY')}</span>`};
    case 'time':
      return moment(value).local().format('hh:mm:ss');
    case 'datetime':
      m = moment(value).local();
      return {template: `<span title="${m.fromNow()}">${m.format('DD/MM/YYYY HH:mm')}</span>`};
    case 'id':
      if (typeof schema.view === 'function') {
        return {
          template: i18n.nn`<a title="${value}" @click="view" href="javascript:void(0)">${value}...</a>`,
          methods: {
            view() {
              schema.view
                .call(this.$parent, model, index, schema, columns)
                .catch(err => {
                  err = parseError(err);
                  this.$notify({
                    component: {
                      template: i18n.f('alert.html.$err', {err}),
                    },
                    icon: 'ti-alert',
                    horizontalAlign: 'center',
                    verticalAlign: 'top',
                    type: 'danger',
                  });
                });
            },
          },
        };
      } else {
        return {template: i18n.nn`<span title="${value}">${value}...</span>`};
      }
    case 'tags':
      return value ? value.join(', ') : '';
    case 'schema':
      return fieldSpec.schema.title.call(this, value, fieldSpec.schema);
    case 'select':
      if (isArray(fieldSpec.items)) {
        return get(find(fieldSpec.items, x => x.value === value), 'label');
      }
      return '';
    case 'array':
      return map(value, (x, idx) => {
        return displayField.call(this, value, 0, {...fieldSpec.itemType, prop: idx}, schema, columns);
      }).join(', ');
    case 'i18n':
      return displayField.call(this, value, 0, {...fieldSpec.itemType, prop: i18n.local}, schema, columns);
  }
  return value;
}

/**
 * @return {object} field spec
 */
export function findKeyField(columns) {
  return find(columns, x => x.type === 'id');
}

/**
 * apply defaults based on field type
 * @param {Object|Array.<Object>} spec
 * @return {Object|Array.<Object>} updated fieldSpec
 */
export function applyFieldDefaults(spec) {
  if (isArray(spec)) {
    return map(spec, applyFieldDefaults);
  }
  let defaults = fieldDefaults[spec.type];
  if (defaults) {
    let clone = {...defaults};
    let fn = clone.postprocess;
    delete clone['postprocess'];
    spec = defaultsDeep(spec, clone);
    if (typeof fn === 'function') {
      spec = fn(spec);
    }
  }
  return spec;
}

/**
 * optional handler, called when user click on table row and redirect to item details
 * @this {Vue} table component
 * @param {String|null} [urlTemplate] url template contains :id
 * @param {Boolean} [newWindow] open in new window, default false
 * @return {view}
 */
export function viewFn(urlTemplate) {
  return function (model, index, schema, columns, newWindow) {
    let url;
    let id;
    if (typeof model === 'string') {
      id = model;
    } else {
      let field = findKeyField(columns);
      if (field) {
        id = getField.call(this, model, field);
      }
    }
    if (id) {
      if (urlTemplate != null) {
        url = urlTemplate.replace(':id', id);
      } else {
        url = trimEnd(this.$route.path, '/') + '/' + id;
      }
      if (newWindow || (window.event && (window.event.ctrlKey || window.event.shiftKey || window.event.metaKey))) {
        // open in new window
        let a = document.createElement("a");
        a.target = "_blank";
        a.href = url;
        document.body.appendChild(a);
        a.click();
        setTimeout(() => {
          document.body.removeChild(a);
        }, 100);
      } else {
        this.$router.push(url);
      }
    }
    return Promise.resolve();
  };
}

export function createFn(urlTemplate) {
  return function (schema, columns) {
    let id = 'create';
    let url;
    if (urlTemplate != null) {
      url = urlTemplate.replace(':id', id);
    } else {
      url = trimEnd(this.$route.path, '/') + '/' + id;
    }
    this.$router.push(url);
    return Promise.resolve();
  };
}

import ExportDialog from 'src/components/UIComponents/ExportDialog';

export function exportFn(fields, processRow) {
  return function (schema, columns) {
    let self = this;
    return MessageBox({
      title: i18n.fr('action.export_$obj', {obj: schema.title.call(self, null, schema)}),
      type: null,
      showCancelButton: false,
      showConfirmButton: false,
      closeOnPressEscape: false,
      closeOnClickModal: false,
      message: self.$createElement(ExportDialog, {
        key: +new Date(), // required to force regenerate the vnode
        props: {
          schema,
          columns,
          fields,
          processRow,
          $rootTable: self,
        },
      }),
    }).then(() => {
    }, () => {
    });
  };
}

/**
 * @param {String} endpoint
 * @param {Function|null} [preprocess] if specified, apply this function on model before submit
 * @param {Function|Object} [httpOptions] options pass to Vue.http.post
 * @param {Function} [postprocess] the callback will be passed a promise as the only argument,
 * @return {list}
 */
export function listFn(endpoint, preprocess, httpOptions, postprocess) {
  return function list({skip, limit, status, ...options}, schema, columns) {
    if (!skip || skip < 0) skip = 0;
    if (!limit || limit < 0) limit = 9999;
    if (status === undefined) status = 'active';

    let payload = {endpoint, skip, limit, status, ...options};
    let _httpOptions;
    if (typeof httpOptions === 'function') {
      _httpOptions = httpOptions.call(this, payload, schema, columns);
    } else {
      _httpOptions = httpOptions;
    }
    if (typeof preprocess === 'function') {
      payload = preprocess.call(this, payload, schema, columns);
    }
    let p = Vue.http.post('', payload, _httpOptions);
    if (typeof postprocess === 'function') {
      return postprocess.call(this, p);
    } else {
      return p.then(response => {
        let result = response.body;
        if (!isArray(result.data)) {
          result.data = [];
        }
        if (result.total == null) {
          result.total = result.data.length;
        }
        return result;
      });
    }
  };
}

/**
 * this function mutates <code>model</code>
 * if <code>this instanceof Vue</code>, <code>model</code> will be mutated with <code>this.$set</code>
 * @this {Vue}
 * @param model
 * @param columns
 * @return {Promise.<Object>} model
 */
export function sanitizeModel(model, columns) {
  let wait = [];
  let self = this;
  for (let col of columns) {
    let value = getField.call(self, model, col);
    if (value == null && col.default != null) {
      let def = typeof col.default === 'function' ? col.default.call(self, model, col) : col.default;
      // support promise
      if (typeof def === 'object' && typeof def.then === 'function') {
        wait.push(def.then(x => setField.call(self, model, col, x)));
      } else {
        setField.call(self, model, col, def);
      }
    } else {
      if (col.type === 'array' || col.type === 'tags' || (col.type === 'select' && col.multi)) {
        if (!isArray(value)) {
          setField.call(self, model, col, []);
        }
      } else if (col.type === 'schema' && !col.external) {
        if (typeof value !== 'object' || value == null) {
          if (col.readonly && value == null) {
            continue;
          }
          setField.call(self, model, col, {});
          value = getField.call(self, model, col);
        }
        let temp = typeof col.schema.columns === 'function'
          ? col.schema.columns.call(self, model, col.schema, col.readonly, false)
          : col.schema.columns;
        wait.push(sanitizeModel.call(self, value, temp));
      } else if (col.type === 'i18n') {
        if (typeof value !== 'object' || value == null) {
          setField.call(self, model, col, {});
        }
      } else if (col.type === 'json') {
        if (typeof value === 'undefined') {
          setField.call(self, model, col, null);
        }
      }
    }
  }
  return Promise.all(wait).then(() => model);
}

/**
 *
 * @param {String} endpoint
 * @param {String} keyName the name fo the id property
 * @param {Function|null} [preprocess] if specified, apply this function on payload before submit
 * @param {Function|Object} [httpOptions] options pass to Vue.http.post
 * @param {Function} [postprocess] the callback will be passed a promise as the only argument,
 * @return {read}
 */
export function readFn(endpoint, keyName, preprocess, httpOptions, postprocess) {
  return function read(id, schema, columns) {
    let payload = {endpoint, [keyName]: id};
    let _httpOptions;
    if (typeof httpOptions === 'function') {
      _httpOptions = httpOptions.call(this, payload, schema, columns);
    } else {
      _httpOptions = httpOptions;
    }
    if (typeof preprocess === 'function') {
      payload = preprocess.call(this, payload, schema, columns);
    }
    let p = Vue.http.post('', payload, _httpOptions);
    if (typeof postprocess === 'function') {
      return postprocess.call(this, p);
    } else {
      return p.then(response => {
        let result = response.body;
        if (!result.data) {
          console.error(response);
          throw new Error(i18n.f('alert.invalid_response', {response}));
        }
        return result.data;
      });
    }
  };
}

/**
 * find a field spec recursively. this function creates a cached finder which will call
 * schema.columns function once only for each depth of each sub schema
 * @param {object} model root model
 * @param {object} schema root schema
 * @param {boolean} [readonly]
 */
export function fieldSpecFinder(model, schema, readonly) {
  let map = {};

  let walkColumn = (path, _model, spec, target, cacheOnly) => {
    let key = path.join('.');
    map[key] = spec;
    if (target === key || cacheOnly) {
      // target reached, stop
      return true;
    }
    if (spec.type === 'array') {
      let value = getField.call(this, _model, spec);
      if (isArray(value)) {
        let found = false;
        for (let i = 0; i < value.length; ++i) {
          let p2 = path.concat([i]);
          found |= walkColumn(p2, _model, spec.itemType, target, found);
        }
        return found;
      }
    } else if (spec.type === 'schema') {
      let value = getField.call(this, _model, spec);
      if (walkSchema(path, value, spec.schema, target)) {
        return true;
      }
    }
  };
  let walkSchema = (path, _model, _schema, target) => {
    let columns = typeof _schema.columns === 'function'
      ? _schema.columns.call(this, _model, _schema, readonly)
      : _schema.columns;
    columns = columns || [];
    let found = false;
    for (let spec of columns) {
      let p2 = path.concat([spec.prop]);
      found |= walkColumn(p2, _model, spec, target, found);
      // do not return immediately. let the loop complete so that fields in the same depth will
      // not call schema.columns multiple times
    }
    return found;
  };

  // searcher function
  return (targetPath) => {
    // normalize path
    if (isArray(targetPath)) {
      targetPath = targetPath.join('.');
    }

    if (targetPath === '') {
      return undefined; // root is not a field
    }

    // try cache
    if (!map[targetPath]) {
      // missed, walk again
      walkSchema([], model, schema, targetPath);
    }

    // it should be available now
    return map[targetPath];
  };
}

/**
 * @param {String} endpoint
 * @param {String} [keyName]
 * @param {Array.<String>|Object.<{payload_prop: model_prop}>|null} [props]
 * property paths projection of model to submit, or null to submit all
 * @param {Function|null} [preprocess] if specified, apply this function on model before submit
 * @param {Function|Object} [httpOptions] options pass to Vue.http.post
 * @param {Function} [postprocess] the callback will be passed a promise as the only argument,
 * and should also return a promise
 * @return {Function.<Promise>}
 */
export function updateFn(endpoint, keyName, props, preprocess, httpOptions, postprocess) {
  return async function update(model, schema, columns) {
    let payload = {};
    let specFinder = fieldSpecFinder(model, schema, false);
    if (props) {
      if (isArray(props)) {
        for (let path of props) {
          path = toPath(path);
          let spec = specFinder(path);
          let parentPath = path.slice(0, -1);
          let parentSpec = specFinder(parentPath);
          // schema need to call getField with sub model as root
          if (parentSpec && parentSpec.type === 'schema') {
            let parent = get(model, parentPath);
            set(payload, path, getField.call(this, parent, {...spec, prop: path.slice(-1)}));
          } else if (spec) {
            set(payload, path, getField.call(this, model, {...spec, prop: path}));
          } else {
            // simple get
            set(payload, path, get(model, path));
          }
        }
      } else {
        for (let key of Object.keys(props)) {
          let spec = specFinder(props[key]);
          if (!spec) {
            // simple get
            set(payload, key, get(model, key));
          } else {
            set(payload, key, getField.call(this, model, spec));
          }
        }
      }
    } else {
      payload = model;
    }
    payload.endpoint = endpoint;
    if (keyName) {
      payload[keyName] = get(model, keyName);
    }
    let _httpOptions;
    if (typeof httpOptions === 'function') {
      _httpOptions = httpOptions.call(this, model, schema, columns);
    } else {
      _httpOptions = httpOptions;
    }
    if (typeof preprocess === 'function') {
      payload = await preprocess.call(this, payload, schema, columns);
      if (payload === false) { // cancel
        return Promise.resolve(false); // don't reload
      }
    }

    let p = Vue.http.post('', payload, _httpOptions);
    if (typeof postprocess === 'function') {
      return postprocess.call(this, p);
    } else {
      return formActions.postprocess.notify(true).call(this, p);
    }
  };
}

export const formActions = {
  get back() {
    return {
      label: i18n.fr('action.back'),
      class: 'btn-info',
      validation: false,
      action(model, schema) {
        this.$router.back();
        return Promise.resolve();
      },
    };
  },
  /**
   * generate a delete function
   * @param {string} endpoint
   * @param {string} key
   * @param {Function|string|Object} redirect
   * @return {Function<Promise>}
   */
  delete(endpoint, key, redirect) {
    let fn = updateFn(
      endpoint,
      key,
      [],
      null,
      null,
      formActions.postprocess.redirect(typeof redirect === 'function' ? redirect : () => redirect));

    return function (model, schema, columns) {
      let args = arguments;
      let self = this;
      let keyField = findKeyField(columns);
      return sweetalert({
        title: i18n.fr('alert.html.confirm_delete_$str', {str: schema.title.call(this, model, schema)}),
        text: keyField ? '(' + getField.call(this, model, keyField) + ')' : null,
        type: 'warning',
        showCancelButton: true,
        confirmButtonText: i18n.fr('action.yes'),
        cancelButtonText: i18n.fr('action.cancel'),
        confirmButtonClass: 'btn btn-danger btn-fill',
        cancelButtonClass: 'btn btn-fill',
        buttonsStyling: false,
      })
        .then(() => {
          return fn.apply(self, args);
        }, () => {
        });
    };
  },
  postprocess: {
    /**
     * generate a postprocess callback which will show a success notification
     * @param reload
     * @return {Function}
     */
    notify(reload) {
      return function (p) {
        let self = this;
        return p.then(response => {
          if (self.$notify) {
            self.$notify({
              component: {
                template: i18n.fr('alert.html.record_saved_successfully', {response}),
              },
              icon: 'ti-info',
              horizontalAlign: 'center',
              verticalAlign: 'top',
              type: 'success',
            });
          }
          return reload;
        });
      };
    },
    /**
     * generate a postprocess callback which will redirect to a link if success
     * @param {Function} link the callback will be passed response.body, and should return a
     * {@link VueRouter.Route} (or anything compatible)
     */
    redirect(link) {
      return function (p) {
        return p.then(response => {
          if (!response.body) {
            throw new Error(i18n.f('alert.empty_response'));
          }
          let result = response.body;
          if (!result.success) {
            console.error(response);
            return Promise.reject(result.err.hk || result.err);
          }
          if (this.$notify) {
            this.$notify({
              component: {
                template: i18n.f('alert.html.record_saved_successfully'),
              },
              icon: 'ti-info',
              horizontalAlign: 'center',
              verticalAlign: 'top',
              type: 'success',
            });
          }
          this.$router.push(link.call(this, result));
          return false; // don't reload
        });
      };
    },
  },
};



// WEBPACK FOOTER //
// ./src/schemas/utils.js